import { testFileName } from '../../../utility/urlHelpers.new';
import { ERROR_INVALID_FILE_NAME_NODE } from '../constants/errors';
import {
    paramClear,
    paramCreateDirectory,
    paramDsName,
    paramName,
    paramNameXPath,
    paramOverwrite,
    paramPath,
    paramUniqueName,
    paramXPath,
} from '../constants/parameterNames';
import {
    typeBoolean,
    typeDataSource,
    typeXPath,
} from '../constants/parameterTypes';
import {
    clearFieldAndSetLinkAsync,
    getBase64ValuesForNode,
    verifyRequiredAttributesExist,
} from '../helpers/attachmentHelpers';
import { commandResult, success } from '../helpers/commandResult';
import { handleError } from '../helpers/errorHandling';
import {
    getBoolParamValue,
    getParamValue,
} from '../helpers/parameter.accessors';
import { getDataSource } from '../helpers/qRules.dataSource.helpers';
import { getValidNode, getValidNodeSet } from '../helpers/qRules.node.helpers';

const requiredParameters = [
    {
        name: paramPath,
        description: 'Path to the destination directory',
    },
    {
        name: paramXPath,
        description: 'XPath to File(s)',
        type: typeXPath,
        dsParentName: paramDsName,
    },
];

const optionalParameters = [
    {
        name: paramDsName,
        description: 'Data Source Name',
        type: typeDataSource,
    },
    {
        name: paramName,
        description:
            'Name for file (extension will be added by qRules if it the value does not contain an extension)',
    },
    {
        name: paramNameXPath,
        description:
            "Path to each file's respective filename, relative to the file fields' location",
        type: typeXPath,
        dsParentName: paramDsName,
        relativeTo: paramXPath,
    },
    {
        name: paramOverwrite,
        description:
            '(boolean, false by default) - Overwrite existing file if name is specified',
        type: typeBoolean,
    },
    {
        name: paramClear,
        description:
            '(boolean, true by default) - Indicates whether to clear the attachment field on successful upload',
        type: typeBoolean,
    },
    {
        name: paramUniqueName,
        description:
            '(boolean, true by default) - Prepend date and time information to file names to provide unique names',
        type: typeBoolean,
    },
    {
        name: paramCreateDirectory,
        description:
            '(boolean, true by default) - Create directory if it does not exist',
        type: typeBoolean,
    },
];

function getFileName(
    cf,
    fileNode,
    fileProps,
    fileNameXPath,
    fileName,
    uniqueName,
) {
    if (fileNameXPath) {
        var fileNameNode = getValidNode(
            fileNode,
            fileNameXPath,
            ERROR_INVALID_FILE_NAME_NODE,
        );

        return fileNameNode.value();
    }
    if (fileName) {
        return fileName;
    }
    if (!uniqueName) {
        return fileProps.fileName;
    }

    return cf.createUniqueTimestamp() + '_' + fileProps.fileName;
}

function validateFileName(fileName, fileProps) {
    if (!fileName) {
        throw new Error('Filename is required to upload file.');
    }

    testFileName(fileName);

    if (fileName.indexOf('.') < 0) {
        return fileName + fileProps.fileExtension;
    }

    var periodSplits = fileName.split('.'),
        lastPeriodVal = periodSplits.pop().toLowerCase();

    if (lastPeriodVal === fileProps.fileExtensionWithoutPeriod) {
        return fileName;
    }

    return fileName + fileProps.fileExtension;
}

const uploadNetworkFileAsync = async (
    qfs,
    fileNode,
    fileNameToUse,
    base64,
    cmdParams,
) => {
    const data = await qfs.uploadNetworkFileAsync(
        cmdParams.createDirectory,
        fileNameToUse,
        base64,
        cmdParams.overwrite,
        cmdParams.path,
    );

    if (!data.success) {
        const errMsg =
            data.error?.Message || 'Unspecified error while uploading the file';

        throw new Error(errMsg);
    }

    await clearFieldAndSetLinkAsync(
        fileNode,
        fileNameToUse,
        data.data,
        cmdParams.clear,
    );
};

const getCommandParams = (parameters) => ({
    path: getParamValue(parameters, paramPath),
    nodeXPath: getParamValue(parameters, paramXPath),
    dsName: getParamValue(parameters, paramDsName),
    name: getParamValue(parameters, paramName),
    nameXPath: getParamValue(parameters, paramNameXPath),
    overwrite: getBoolParamValue(parameters, paramOverwrite),
    clear: getBoolParamValue(parameters, paramClear, true),
    uniqueName: getBoolParamValue(parameters, paramUniqueName, true),
    createDirectory: getBoolParamValue(parameters, paramCreateDirectory, true),
});

function SaveToNetwork(params) {
    const executeAsync = async ({ api, dataSources, parameters }) => {
        const cf = params.commonFunctions;
        const cp = getCommandParams(parameters);

        const dom = getDataSource(dataSources, cp.dsName);
        const fileNodes = getValidNodeSet(dom, cp.nodeXPath);
        const nonBlankFileNodes = fileNodes.filter((n) => n.value());

        if (!nonBlankFileNodes.length) {
            return success(
                '0',
                'Warning: No attachments were found at the specified path.',
            );
        }

        try {
            let uploadCnt = 0;

            for (const fileNode of fileNodes) {
                verifyRequiredAttributesExist(fileNode);

                const fileProps = getBase64ValuesForNode(fileNode);

                if (!fileProps.base64) {
                    throw new Error('Failed to upload, invalid file found.');
                }

                const fileName = getFileName(
                    cf,
                    fileNode,
                    fileProps,
                    cp.nameXPath,
                    cp.name,
                    cp.uniqueName,
                );
                const fileNameToUse = validateFileName(fileName, fileProps);

                await uploadNetworkFileAsync(
                    api,
                    fileNode,
                    fileNameToUse,
                    fileProps.base64,
                    cp,
                );

                uploadCnt += 1;
            }

            return commandResult(uploadCnt > 0, uploadCnt.toString());
        } catch (e) {
            return handleError(e);
        }
    };

    return {
        executeAsync,
        optionalParameters,
        requiredParameters,
    };
}

export default SaveToNetwork;
